import { Injectable, inject } from '@angular/core';
import { PlatformService } from './platform.service';
import { TranslationService } from './translation.service';
import { ErrorReportingService } from './error-reporting.service';
import { RoutineSchedule, TOOL_BY_ID } from '../shell/shell.models';

/**
 * Routine reminders.
 *
 * The profile screen lets her turn a tool on and pick a frequency and a time,
 * and it tells her "Next reminder: every day in the morning". Until now nothing
 * ever delivered that: the schedule was only used to build the home plan, so
 * the promise on screen was never kept. This service is what keeps it.
 *
 * Native only, and deliberately LOCAL (@capacitor/local-notifications): no
 * server, no push token, no backend cron. The OS holds the repeating alarms, so
 * they keep firing with the app closed and survive a restart. On web there is
 * no reliable background delivery without a service worker, so we no-op.
 */

/** Hour of day per time-of-day slot. 'anytime' lands mid-morning. */
const HOUR: Record<string, number> = {
  morning: 9,
  midday: 13,
  evening: 19,
  anytime: 10,
};

/**
 * Weekdays a frequency fires on (Capacitor weekday: 1 = Sunday).
 * Matches RoutineService.getTodayPlan so the reminder lands on the same days
 * the home plan lists the tool.
 */
function weekdaysFor(freq: string): number[] | null {
  if (freq === 'weekly') return [1]; // Sunday
  if (freq === '2-3x') return [1, 3, 5]; // Sun, Tue, Thu
  return null; // daily -> one repeating entry, no weekday pinning
}

/** Stable positive 32-bit id per (tool, slot) so a reschedule replaces itself. */
function notifId(toolId: string, slot: number): number {
  let h = 5381;
  for (let i = 0; i < toolId.length; i++) h = ((h * 33) ^ toolId.charCodeAt(i)) >>> 0;
  return ((h % 1000000) * 10 + slot + 1000) % 2147483647;
}

@Injectable({ providedIn: 'root' })
export class RoutineReminderService {
  private platform = inject(PlatformService);
  private translation = inject(TranslationService);
  private reporter = inject(ErrorReportingService);

  /** True once the OS has granted us permission to post notifications. */
  private async ensurePermission(): Promise<string> {
    try {
      const { LocalNotifications } = await import('@capacitor/local-notifications');
      let perm = await LocalNotifications.checkPermissions();
      if (perm.display === 'prompt' || perm.display === 'prompt-with-rationale') {
        perm = await LocalNotifications.requestPermissions();
      }
      return perm.display;
    } catch {
      return 'error';
    }
  }

  /**
   * Re-scheduling is always full: cancel everything we own, then lay down the
   * current schedule. Cheap, and it can never leave a stale reminder behind for
   * a tool she switched off.
   */
  async sync(schedule: RoutineSchedule): Promise<void> {
    if (!this.platform.isNative) return;
    try {
      const { LocalNotifications } = await import('@capacitor/local-notifications');

      const active = Object.keys(schedule || {}).filter(
        (id) => schedule[id]?.active && TOOL_BY_ID[id],
      );

      const pending = await LocalNotifications.getPending();
      const ours = new Set<number>();
      Object.keys(TOOL_BY_ID).forEach((id) => {
        for (let slot = 0; slot < 3; slot++) ours.add(notifId(id, slot));
      });
      const stale = (pending?.notifications || []).filter((n) => ours.has(n.id));
      if (stale.length) await LocalNotifications.cancel({ notifications: stale });

      if (!active.length) {
        this.reporter.logEvent('reminders.sync', { active: 0, cancelled: stale.length });
        return;
      }
      // Breadcrumb either way: a silent "denied" is otherwise invisible to us.
      const perm = await this.ensurePermission();
      if (perm !== 'granted') {
        this.reporter.logEvent('reminders.sync', { active: active.length, perm, scheduled: 0 });
        return;
      }

      // Launch sync can run before i18n is loaded; t() then returns empty text
      // and the OS shows a blank notification. Wait for the strings first.
      await this.translation.waitForInit();
      const notifications: any[] = [];
      active.forEach((id) => {
        const cfg = schedule[id];
        const hour = HOUR[cfg.time] ?? HOUR['anytime'];
        const days = weekdaysFor(cfg.freq || 'daily');
        const { title, body } = this.copyFor(id);
        const at = days ? days : [null];
        at.forEach((weekday, slot) => {
          notifications.push({
            id: notifId(id, slot),
            title,
            body,
            schedule: {
              on: weekday === null ? { hour, minute: 0 } : { weekday, hour, minute: 0 },
              allowWhileIdle: true,
            },
            extra: { toolId: id },
          });
        });
      });

      if (notifications.length) await LocalNotifications.schedule({ notifications });
      this.reporter.logEvent('reminders.sync', {
        active: active.length,
        perm,
        scheduled: notifications.length,
      });
    } catch (e) {
      console.error('routine reminders: scheduling failed:', e);
      this.reporter.report(e, 'routine-reminders');
    }
  }

  /**
   * Per-tool wording that invites her into that tool ("Had some water yet?"),
   * falling back to the generic routine nudge for a tool without its own copy.
   */
  private copyFor(id: string): { title: string; body: string } {
    const base = 'shell.profile.reminder.tools.' + id;
    const title = this.translation.translate(base + '.title');
    const body = this.translation.translate(base + '.body');
    if (title && title !== base + '.title' && body && body !== base + '.body') {
      return { title, body };
    }
    return {
      title: this.translation.translate('shell.profile.reminder.title'),
      body: this.translation.translate('shell.profile.reminder.body', {
        tool: this.translation.translate('shell.tools.' + id + '.name'),
      }),
    };
  }

  /** Turn every routine reminder off (used when nothing is active). */
  async clearAll(): Promise<void> {
    await this.sync({});
  }
}
